import { type IUserProfileAttribute } from './user-profile-attribute._._definitions.js'
import { type IUserProfileInterest } from './user-profile-interest._._definitions.js'
import { type IUser } from './user._._definitions.js'

export interface IUserProfile {
	id: string
	userId: string
	user?: IUser
	attributes?: IUserProfileAttribute[]
	interests?: IUserProfileInterest[]
	createdAt: Date
	updatedAt: Date
	// Demographics - fields that exist in current schema
	bio?: string | null
	dob?: Date | null
	ageCohort?: string | null
	gender?: string | null
	location?: string | null
	regionId?: string | null
	regionRef?: { displayName: string } | null
	zipCode?: string | null
	educationLevel?: string | null
	school?: string | null
	industry?: string | null
	company?: string | null
	jobTitle?: string | null
	relationshipStatus?: string | null
	childStatus?: string | null
	pets?: string | null
	pronouns?: string | null
	sexualOrientation?: string | null
	ethnicity?: string | null
	religion?: string | null
	politicalAffiliation?: string | null
	isVeteran?: boolean | null
	routine?: string | null
	homeStateOrCountry?: string | null
	partOfTown?: string | null // from main - part of town location
	alcoholConsumption?: string | null

	// Structured location data (for geographic recommendations)
	address?: string | null
	city?: string | null
	state?: string | null
	distanceWillingToTravel?: number | null

	// Onboarding-specific fields (already in schema)
	travelDistance?: string | null
	availabilityTimes?: string | null // JSON array of available times
	hasKids?: boolean | null
	workDescription?: string | null
	learningInterests?: string | null
	activityRatings?: string | null // JSON object of activity preferences
	desiredFriendAttributes?: string | null // JSON array of desired friend traits
	avoidedFriendAttributes?: string | null // JSON array of avoided friend traits
	preferredBeverage?: string | null
	monthlyHangoutGoal?: string | null // 1-2, 3-4, more-than-4, not-sure-yet
	questionnaireCompletedAt?: Date | null
	livingArrangement?: string | null
	planAheadWindow?: string | null
	maxHangsPerMonth?: number | null
}

export const USER_PROFILE_AGE_COHORT = {
	'25_29': '25-29',
	'30_34': '30-34',
	'35_39': '35-39',
	'40_44': '40-44',
	'45_49': '45-49',
	'50_plus': '50+', // Using valid object key format from dev
} as const
export type IUserProfileAgeCohort =
	(typeof USER_PROFILE_AGE_COHORT)[keyof typeof USER_PROFILE_AGE_COHORT]
export const USER_PROFILE_AGE_COHORT_VALUES: string[] = Object.values(
	USER_PROFILE_AGE_COHORT,
)

export const USER_PROFILE_LOCATION = {
	WASHINGTON_DC: 'washington-d.c.',
	NEW_YORK_CITY: 'new-york-city',
	OTHER: 'other',
} as const
export type IUserProfileLocation =
	(typeof USER_PROFILE_LOCATION)[keyof typeof USER_PROFILE_LOCATION]
export const USER_PROFILE_LOCATION_VALUES: string[] = Object.values(
	USER_PROFILE_LOCATION,
)
export const USER_PROFILE_APPROVED_LOCATION_VALUES: string[] = [
	USER_PROFILE_LOCATION.WASHINGTON_DC,
	USER_PROFILE_LOCATION.NEW_YORK_CITY,
]

export const USER_PROFILE_GENDER = {
	FEMALE: 'female',
	MALE: 'male',
	// NON_BINARY: 'non-binary',
	// TRANSGENDER: 'transgender',
	OTHER: 'other',
	// PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileGender =
	(typeof USER_PROFILE_GENDER)[keyof typeof USER_PROFILE_GENDER]
export const USER_PROFILE_GENDER_VALUES: string[] =
	Object.values(USER_PROFILE_GENDER)

export const USER_PROFILE_PRONOUNS = {
	HE: 'he/him',
	SHE: 'she/her',
	THEY: 'they/them',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfilePronouns =
	(typeof USER_PROFILE_PRONOUNS)[keyof typeof USER_PROFILE_PRONOUNS]
export const USER_PROFILE_PRONOUNS_VALUES: string[] = Object.values(
	USER_PROFILE_PRONOUNS,
)

export const USER_PROFILE_SEXUAL_ORIENTATION = {
	STRAIGHT: 'straight',
	GAY: 'gay',
	BISEXUAL: 'bisexual',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileSexualOrientation =
	(typeof USER_PROFILE_SEXUAL_ORIENTATION)[keyof typeof USER_PROFILE_SEXUAL_ORIENTATION]
export const USER_PROFILE_SEXUAL_ORIENTATION_VALUES: string[] = Object.values(
	USER_PROFILE_SEXUAL_ORIENTATION,
)

export const USER_PROFILE_ETHNICITY = {
	WHITE: 'white/caucasian',
	AFRICAN_AMERICAN: 'black/african-descent',
	EAST_ASIAN: 'east-asian',
	SOUTH_ASIAN: 'south-asian',
	PACIFIC_ISLANDER: 'pacific-islander',
	HISPANIC: 'hispanic/latino',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileEthnicity =
	(typeof USER_PROFILE_ETHNICITY)[keyof typeof USER_PROFILE_ETHNICITY]
export const USER_PROFILE_ETHNICITY_VALUES: string[] = Object.values(
	USER_PROFILE_ETHNICITY,
)

export const USER_PROFILE_EDUCATION_LEVEL = {
	HIGH_SCHOOL: 'high-school',
	ASSOCIATES: 'associates',
	BACHELORS: 'bachelors',
	MASTERS: 'masters',
	DOCTORATE: 'doctorate',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileEducationLevel =
	(typeof USER_PROFILE_EDUCATION_LEVEL)[keyof typeof USER_PROFILE_EDUCATION_LEVEL]
export const USER_PROFILE_EDUCATION_LEVEL_VALUES: string[] = Object.values(
	USER_PROFILE_EDUCATION_LEVEL,
)

export const USER_PROFILE_RELATIONSHIP_STATUS = {
	SINGLE: 'single',
	IN_A_RELATIONSHIP: 'in-a-relationship',
	MARRIED: 'married',
	DIVORCED: 'divorced',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileRelationshipStatus =
	(typeof USER_PROFILE_RELATIONSHIP_STATUS)[keyof typeof USER_PROFILE_RELATIONSHIP_STATUS]
export const USER_PROFILE_RELATIONSHIP_STATUS_VALUES: string[] = Object.values(
	USER_PROFILE_RELATIONSHIP_STATUS,
)


export const USER_PROFILE_RELIGION = {
	CHRISTIAN: 'christian',
	JEWISH: 'jewish',
	MUSLIM: 'muslim',
	BUDDHIST: 'buddhist',
	HINDU: 'hindu',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileReligion =
	(typeof USER_PROFILE_RELIGION)[keyof typeof USER_PROFILE_RELIGION]
export const USER_PROFILE_RELIGION_VALUES: string[] = Object.values(
	USER_PROFILE_RELIGION,
)

export const USER_PROFILE_POLITICAL_AFFILIATION = {
	FAR_LEFT: 'far-left',
	DEMOCRAT: 'democrat',
	INDEPENDENT_LIBERAL: 'independent-liberal',
	INDEPENDENT_MODERATE: 'independent-moderate',
	INDEPENDENT_CONSERVATIVE: 'independent-conservative',
	REPUBLICAN: 'republican',
	FAR_RIGHT: 'far-right',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfilePoliticalAffiliation =
	(typeof USER_PROFILE_POLITICAL_AFFILIATION)[keyof typeof USER_PROFILE_POLITICAL_AFFILIATION]
export const USER_PROFILE_POLITICAL_AFFILIATION_VALUES: string[] =
	Object.values(USER_PROFILE_POLITICAL_AFFILIATION)

export const USER_PROFILE_ROUTINE = {
	MORNING_PERSON: 'morning-person',
	NIGHT_OWL: 'night-owl',
	ALWAYS_ON: 'always-on',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfileRoutine =
	(typeof USER_PROFILE_ROUTINE)[keyof typeof USER_PROFILE_ROUTINE]
export const USER_PROFILE_ROUTINE_VALUES: string[] =
	Object.values(USER_PROFILE_ROUTINE)

export const USER_PROFILE_PETS = {
	NO_PETS: 'no-pets',
	DOG_OWNER: 'dog-owner',
	CAT_OWNER: 'cat-owner',
	DOG_AND_CAT_OWNER: 'dog-and-cat-owner',
	OTHER: 'other',
	PREFER_NOT_TO_SAY: 'prefer-not-to-say',
} as const
export type IUserProfilePets =
	(typeof USER_PROFILE_PETS)[keyof typeof USER_PROFILE_PETS]
export const USER_PROFILE_PETS_VALUES: string[] =
	Object.values(USER_PROFILE_PETS)

export const USER_PROFILE_ALCOHOL_CONSUMPTION = {
	YES: 'yes',
	ONLY_SOCIALLY: 'only-socially',
	NO_DOES_NOT_MIND: 'no-does-not-mind',
	NO_PREFERS_ALCOHOL_FREE: 'no-prefers-alcohol-free',
} as const
export type IUserProfileAlcoholConsumption =
	(typeof USER_PROFILE_ALCOHOL_CONSUMPTION)[keyof typeof USER_PROFILE_ALCOHOL_CONSUMPTION]
export const USER_PROFILE_ALCOHOL_CONSUMPTION_VALUES: string[] = Object.values(
	USER_PROFILE_ALCOHOL_CONSUMPTION,
)

// New questionnaire-related constants
export const USER_PROFILE_TRAVEL_DISTANCE = {
	UNDER_30_MINUTES: 'under-30-minutes',
	UP_TO_HOUR: 'up-to-hour',
	ANYWHERE_IF_WORTH_IT: 'anywhere-if-worth-it',
} as const
export type IUserProfileTravelDistance =
	(typeof USER_PROFILE_TRAVEL_DISTANCE)[keyof typeof USER_PROFILE_TRAVEL_DISTANCE]
export const USER_PROFILE_TRAVEL_DISTANCE_VALUES: string[] = Object.values(
	USER_PROFILE_TRAVEL_DISTANCE,
)

export const USER_PROFILE_AVAILABILITY_TIMES = {
	// Specific availability options
	MONDAY_DAY: 'monday-day',
	MONDAY_NIGHT: 'monday-night',
	TUESDAY_DAY: 'tuesday-day',
	TUESDAY_NIGHT: 'tuesday-night',
	WEDNESDAY_DAY: 'wednesday-day',
	WEDNESDAY_NIGHT: 'wednesday-night',
	THURSDAY_DAY: 'thursday-day',
	THURSDAY_NIGHT: 'thursday-night',
	FRIDAY_DAY: 'friday-day',
	FRIDAY_NIGHT: 'friday-night',
	SATURDAY_DAY: 'saturday-day',
	SATURDAY_NIGHT: 'saturday-night',
	SUNDAY_DAY: 'sunday-day',
	SUNDAY_NIGHT: 'sunday-night',
} as const
export type IUserProfileAvailabilityTime =
	(typeof USER_PROFILE_AVAILABILITY_TIMES)[keyof typeof USER_PROFILE_AVAILABILITY_TIMES]
export const USER_PROFILE_AVAILABILITY_TIMES_VALUES: string[] = Object.values(
	USER_PROFILE_AVAILABILITY_TIMES,
)

export const USER_PROFILE_SPECIFIC_AVAILABILITY_TIMES_VALUES: string[] = [
	USER_PROFILE_AVAILABILITY_TIMES.MONDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.MONDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.TUESDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.TUESDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.WEDNESDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.WEDNESDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.THURSDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.THURSDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.FRIDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.FRIDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.SATURDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.SATURDAY_NIGHT,
	USER_PROFILE_AVAILABILITY_TIMES.SUNDAY_DAY,
	USER_PROFILE_AVAILABILITY_TIMES.SUNDAY_NIGHT,
]

export const USER_PROFILE_AVAILABILITY_DAYS = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as const
export type IUserProfileAvailabilityDay = (typeof USER_PROFILE_AVAILABILITY_DAYS)[number]

export const USER_PROFILE_MONTHLY_HANGOUT_GOAL = {
	ONE_TO_TWO: '1-2',
	THREE_TO_FOUR: '3-4',
	MORE_THAN_FOUR: 'more-than-4',
	NOT_SURE_YET: 'not-sure-yet',
} as const
export type IUserProfileMonthlyHangoutGoal =
	(typeof USER_PROFILE_MONTHLY_HANGOUT_GOAL)[keyof typeof USER_PROFILE_MONTHLY_HANGOUT_GOAL]
export const USER_PROFILE_MONTHLY_HANGOUT_GOAL_VALUES: string[] = Object.values(
	USER_PROFILE_MONTHLY_HANGOUT_GOAL,
)

export const USER_PROFILE_PLAN_AHEAD_WINDOW = {
	ANY_TIME_FRAME: 'any-time-frame',
	SPONTANEOUS: 'spontaneous',
	ONE_TO_TWO_WEEKS: '1-2-weeks',
	TWO_TO_THREE_WEEKS: '2-3-weeks',
	THREE_PLUS_WEEKS: '3-plus-weeks',
} as const
export type IUserProfilePlanAheadWindow =
	(typeof USER_PROFILE_PLAN_AHEAD_WINDOW)[keyof typeof USER_PROFILE_PLAN_AHEAD_WINDOW]
export const USER_PROFILE_PLAN_AHEAD_WINDOW_VALUES: string[] = Object.values(
	USER_PROFILE_PLAN_AHEAD_WINDOW,
)
export const USER_PROFILE_PLAN_AHEAD_WINDOW_OPTIONS = [
	{ label: 'Any time frame', value: USER_PROFILE_PLAN_AHEAD_WINDOW.ANY_TIME_FRAME },
	{ label: 'Spontaneous (within a week)', value: USER_PROFILE_PLAN_AHEAD_WINDOW.SPONTANEOUS },
	{ label: '1-2 weeks', value: USER_PROFILE_PLAN_AHEAD_WINDOW.ONE_TO_TWO_WEEKS },
	{ label: '2-3 weeks', value: USER_PROFILE_PLAN_AHEAD_WINDOW.TWO_TO_THREE_WEEKS },
	{ label: '3+ weeks', value: USER_PROFILE_PLAN_AHEAD_WINDOW.THREE_PLUS_WEEKS },
]

export const MAX_HANGS_PER_MONTH_OPTIONS = [
	{ label: 'No limit', value: '' },
	{ label: '1-2', value: '2' },
	{ label: '3-4', value: '4' },
	{ label: '5-6', value: '6' },
]

export function formatMaxHangsPerMonth(val: number | string | null): string {
	if (!val) return 'No limit'
	const n = Number(val)
	if (n <= 2) return '1-2'
	if (n <= 4) return '3-4'
	return '5-6'
}

/**
 * The profile fields the product owner ranked as reasons to invite (#888),
 * which Emma's group introductions count too (#829).
 */
export type TraitProfile = {
	hasKids: boolean | null
	isVeteran: boolean | null
	ageCohort: string | null
}
